import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, CheckCircle2, Clock, Truck, Home as HomeIcon, XCircle, User as UserIcon, BookmarkPlus, Star, Trash2, Pencil } from "lucide-react";
import api, { fmtJMD, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import ImageUpload, { resolveImageUrl } from "../components/ImageUpload";
import StarRating from "../components/StarRating";

const STATUS_META = {
  pending: { label: "Pending", icon: Clock, color: "#F59E0B" },
  processing: { label: "Processing", icon: Package, color: "#3B82F6" },
  processing_complete: { label: "Ready to ship", icon: CheckCircle2, color: "#8B5CF6" },
  in_transit: { label: "In transit", icon: Truck, color: "#06B6D4" },
  delivered: { label: "Delivered", icon: HomeIcon, color: "#10B981" },
  cancelled: { label: "Cancelled", icon: XCircle, color: "#EF4444" },
};

const TABS = [
  { key: "profile", label: "Profile" },
  { key: "boxes", label: "Saved Boxes" },
  { key: "addresses", label: "Addresses" },
  { key: "orders", label: "Orders" },
  { key: "reviews", label: "My Reviews" },
];

export default function CustomerAccount() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("profile");

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-6 md:px-12 py-12 sm:py-16" data-testid="customer-account">
      <div className="flex items-end justify-between mb-8 sm:mb-10 flex-wrap gap-4">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <Avatar user={user} size={64} />
          <div className="min-w-0">
            <div className="overline text-zinc-500 mb-2">Your account</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight break-words">Hi, {user.name}</h1>
            <p className="mt-1 text-zinc-500 break-all text-xs sm:text-sm">{user.email}</p>
          </div>
        </div>
        <button onClick={logout} className="btn-ghost text-sm" data-testid="customer-logout">Sign out</button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-6 border-b border-zinc-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition -mb-px ${
              tab === t.key ? "border-[#F74B64] text-[#F74B64]" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
            data-testid={`account-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <ProfilePanel />}
      {tab === "boxes" && <SavedBoxesPanel />}
      {tab === "addresses" && <AddressesPanel />}
      {tab === "orders" && <OrdersPanel />}
      {tab === "reviews" && <ReviewsPanel />}
    </div>
  );
}

function Avatar({ user, size = 40 }) {
  const url = resolveImageUrl(user.avatar_url);
  if (url) {
    return <img src={url} alt={user.name} className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />;
  }
  const initials = (user.name || user.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-display font-bold flex-shrink-0"
      style={{ width: size, height: size, background: "#F74B64", fontSize: size * 0.4 }}
    >
      {initials}
    </div>
  );
}

/* =================================================
   PROFILE
   ================================================= */
function ProfilePanel() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [avatar, setAvatar] = useState(user.avatar_url || "");
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/account/me", { name, avatar_url: avatar });
      toast.success("Profile updated");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSavingProfile(false); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSavingPw(true);
    try {
      await api.patch("/account/me", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password changed");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSavingPw(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="profile-panel">
      <div className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
        <div className="overline text-zinc-500 mb-4">Profile photo & name</div>
        <ImageUpload value={avatar} onChange={setAvatar} testid="profile-avatar-upload" />
        <div className="mt-4">
          <label className="text-xs font-semibold text-zinc-600 block mb-1">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)}
                 className="w-full border border-zinc-200 rounded-lg px-3 py-2"
                 data-testid="profile-name-input" />
        </div>
        <div className="mt-4">
          <label className="text-xs font-semibold text-zinc-600 block mb-1">Email</label>
          <input value={user.email} disabled
                 className="w-full border border-zinc-200 rounded-lg px-3 py-2 bg-zinc-50 text-zinc-500" />
        </div>
        <button onClick={saveProfile} disabled={savingProfile}
                className="btn-primary w-full mt-5 disabled:opacity-50" data-testid="profile-save-btn">
          {savingProfile ? "Saving…" : "Save profile"}
        </button>
      </div>

      <form onSubmit={savePw} className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6" data-testid="password-form">
        <div className="overline text-zinc-500 mb-4">Change password</div>
        <div className="space-y-3">
          <Input label="Current password" type="password" required
                 value={pwForm.current_password}
                 onChange={(v) => setPwForm({ ...pwForm, current_password: v })} testid="pw-current" />
          <Input label="New password (min 6)" type="password" required minLength={6}
                 value={pwForm.new_password}
                 onChange={(v) => setPwForm({ ...pwForm, new_password: v })} testid="pw-new" />
          <Input label="Confirm new password" type="password" required minLength={6}
                 value={pwForm.confirm}
                 onChange={(v) => setPwForm({ ...pwForm, confirm: v })} testid="pw-confirm" />
        </div>
        <button type="submit" disabled={savingPw}
                className="btn-primary w-full mt-5 disabled:opacity-50" data-testid="pw-save-btn">
          {savingPw ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}

function Input({ label, type = "text", required, minLength, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-600 block mb-1">{label}</label>
      <input type={type} required={required} minLength={minLength} value={value}
             onChange={(e) => onChange(e.target.value)}
             className="w-full border border-zinc-200 rounded-lg px-3 py-2"
             data-testid={testid} />
    </div>
  );
}

/* =================================================
   SAVED BOXES
   ================================================= */
function SavedBoxesPanel() {
  const [boxes, setBoxes] = useState(null);
  const [config, setConfig] = useState({ price_per_pop: 650 });
  const { addItem } = useCart();
  const navigate = useNavigate();

  const load = async () => {
    const r = await api.get("/account/boxes");
    setBoxes(r.data);
    try { const c = await api.get("/build-a-box/config"); setConfig(c.data); } catch {}
  };

  useEffect(() => { load(); }, []);

  const orderAgain = (b) => {
    const total = (b.flavour_names?.length || 0) * (config.price_per_pop || 650);
    addItem({
      type: "custom_box",
      ref_id: null,
      name: `${b.name} · ${b.flavour_names.length} pops`,
      price: total,
      quantity: 1,
      custom_flavours: b.flavour_names,
    });
    toast.success(`${b.name} added to cart`);
    navigate("/cart");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this saved box?")) return;
    try {
      await api.delete(`/account/boxes/${id}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const rename = async (b) => {
    const next = window.prompt("Rename box", b.name);
    if (!next || next === b.name) return;
    try {
      await api.patch(`/account/boxes/${b.id}`, { name: next, flavour_names: b.flavour_names });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  if (boxes === null) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div data-testid="saved-boxes-panel">
      {boxes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-200 p-8 sm:p-10 text-center">
          <BookmarkPlus size={32} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500 mb-4">No saved boxes yet. Build a box and save it to reorder in one tap.</p>
          <Link to="/build" className="btn-primary inline-flex" data-testid="go-build-from-saved">Build a box</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {boxes.map((b) => {
            const total = (b.flavour_names?.length || 0) * (config.price_per_pop || 650);
            const counts = b.flavour_names.reduce((a, n) => ((a[n] = (a[n] || 0) + 1), a), {});
            return (
              <div key={b.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`saved-box-${b.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="font-display font-bold text-lg break-words">{b.name}</div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => rename(b)} className="p-1.5 hover:bg-zinc-100 rounded" title="Rename" data-testid={`rename-box-${b.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(b.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded" title="Delete" data-testid={`delete-box-${b.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-zinc-500">
                  {b.flavour_names.length} pops ·{" "}
                  {Object.entries(counts).map(([n, c]) => `${c}× ${n}`).join(" · ")}
                </div>
                <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="font-display font-bold text-lg">{fmtJMD(total)}</div>
                  <button onClick={() => orderAgain(b)} className="btn-primary text-sm" data-testid={`reorder-box-${b.id}`}>
                    Order again
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* =================================================
   ORDERS
   ================================================= */
function OrdersPanel() {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("active");  // "active" | "history" | "all"

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);

  if (orders === null) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (orders.length === 0)
    return (
      <div className="rounded-2xl border border-dashed border-zinc-200 p-8 sm:p-10 text-center">
        <p className="text-zinc-500 mb-4">You haven't placed any orders yet.</p>
        <Link to="/shop" className="btn-primary inline-flex">Shop flavours</Link>
      </div>
    );

  const historyStatuses = new Set(["delivered", "cancelled"]);
  const visible = orders.filter((o) => {
    if (filter === "history") return historyStatuses.has(o.status);
    if (filter === "active") return !historyStatuses.has(o.status);
    return true;
  });
  const counts = {
    active: orders.filter((o) => !historyStatuses.has(o.status)).length,
    history: orders.filter((o) => historyStatuses.has(o.status)).length,
    all: orders.length,
  };

  return (
    <div className="space-y-4" data-testid="orders-panel">
      {/* Active / History tabs */}
      <div className="inline-flex rounded-full bg-zinc-100 p-1 mb-2 text-xs font-semibold" data-testid="orders-filter">
        {[
          ["active", `Active (${counts.active})`],
          ["history", `History (${counts.history})`],
          ["all", `All (${counts.all})`],
        ].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 rounded-full transition-colors ${
                    filter === k ? "bg-white shadow text-zinc-900" : "text-zinc-500 hover:text-zinc-900"
                  }`}
                  data-testid={`orders-filter-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {visible.length === 0 && (
        <div className="rounded-2xl border border-dashed border-zinc-200 p-8 text-center">
          <p className="text-sm text-zinc-500">
            {filter === "history"
              ? "No completed or cancelled orders yet."
              : filter === "active"
              ? "No active orders right now. Time to grab some pops!"
              : "Nothing here yet."}
          </p>
        </div>
      )}

      {visible.map((o) => {
        const meta = STATUS_META[o.status] || STATUS_META.pending;
        const StepIcon = meta.icon;
        const isHistory = historyStatuses.has(o.status);
        return (
          <Link key={o.id} to={`/order/${o.id}?email=${encodeURIComponent(o.customer_email || "")}`}
                className={`block rounded-2xl border p-5 hover:shadow-lg transition ${
                  isHistory ? "border-zinc-200 bg-zinc-50" : "border-zinc-200 bg-white"
                }`}
                data-testid={`customer-order-${o.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <div className="text-xs text-zinc-500 font-mono">#{o.id.slice(0, 8)}</div>
                <div className="font-display font-bold text-lg mt-0.5">
                  {o.items.length} item{o.items.length !== 1 ? "s" : ""} · {fmtJMD(o.total)}
                </div>
              </div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full"
                   style={{ background: meta.color + "22", color: meta.color }}>
                <StepIcon size={12} /> {meta.label}
              </div>
            </div>
            <div className="text-sm text-zinc-500">
              {(o.fulfillment_method === "pickup")
                ? <>Pickup · Greater Portmore · placed {new Date(o.created_at).toLocaleDateString()}</>
                : <>{o.parish} · {o.delivery_day} · placed {new Date(o.created_at).toLocaleDateString()}</>}
            </div>
            {!isHistory && (
              <div className="mt-4 flex gap-1">
                {["pending", "processing", "processing_complete", "in_transit", "delivered"].map((s) => {
                  const flowKeys = ["pending", "processing", "processing_complete", "in_transit", "delivered"];
                  const done = flowKeys.indexOf(o.status) >= flowKeys.indexOf(s);
                  return <div key={s} className="flex-1 h-1 rounded-full transition-all"
                              style={{ background: done ? "#F74B64" : "#E4E4E7" }} />;
                })}
              </div>
            )}
          </Link>
        );
      })}
    </div>
  );
}

/* =================================================
   MY REVIEWS
   ================================================= */
function ReviewsPanel() {
  const [reviews, setReviews] = useState(null);

  const load = async () => {
    const r = await api.get("/account/reviews");
    setReviews(r.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/reviews/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  if (reviews === null) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (reviews.length === 0)
    return (
      <div className="rounded-2xl border border-dashed border-zinc-200 p-8 sm:p-10 text-center">
        <Star size={32} className="mx-auto text-zinc-300 mb-3" />
        <p className="text-zinc-500">After your order is delivered, you'll be able to rate the flavours here.</p>
      </div>
    );

  return (
    <div className="space-y-3" data-testid="my-reviews-panel">
      {reviews.map((r) => (
        <div key={r.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`my-review-${r.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-display font-bold">{r.product_name || "Flavour"}</div>
              <StarRating rating={r.rating} size={14} className="mt-1" />
              {r.comment && <p className="text-sm text-zinc-600 mt-2 break-words">{r.comment}</p>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <StatusPill status={r.status} />
              <button onClick={() => remove(r.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded" data-testid={`delete-my-review-${r.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { label: "Pending review", bg: "bg-amber-100", color: "text-amber-700" },
    approved: { label: "Live", bg: "bg-green-100", color: "text-green-700" },
    rejected: { label: "Hidden", bg: "bg-red-100", color: "text-red-700" },
  };
  const m = map[status] || map.pending;
  return <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${m.bg} ${m.color}`}>{m.label}</span>;
}

/* =================================================
   ADDRESS BOOK
   ================================================= */
function AddressesPanel() {
  const [addresses, setAddresses] = useState(null);
  const [editing, setEditing] = useState(null);
  const BLANK = { label: "Home", name: "", phone: "", parish: "Kingston", address_line: "", is_default: false };

  const load = async () => setAddresses((await api.get("/account/addresses")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name || !editing.phone || !editing.address_line) {
      toast.error("Name, phone, and address are required"); return;
    }
    try {
      if (editing.id) {
        await api.patch(`/account/addresses/${editing.id}`, editing);
      } else {
        await api.post("/account/addresses", editing);
      }
      toast.success(editing.id ? "Address updated" : "Address saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete '${a.label}' address?`)) return;
    try { await api.delete(`/account/addresses/${a.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  if (addresses === null) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div data-testid="addresses-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-zinc-500">
          Save addresses for faster checkout. Your default address auto-fills next time you order.
        </p>
        <button onClick={() => setEditing({ ...BLANK })}
                className="btn-primary text-sm inline-flex items-center gap-2"
                data-testid="new-address-btn">
          <BookmarkPlus size={14} /> Add address
        </button>
      </div>

      {addresses.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-200 p-8 sm:p-10 text-center">
          <p className="text-zinc-500">No saved addresses yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <div key={a.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`address-${a.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="font-display font-bold flex items-center gap-2 flex-wrap">
                    {a.label}
                    {a.is_default && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#F74B64] text-white">Default</span>}
                  </div>
                  <div className="text-sm text-zinc-600 mt-1 break-words">{a.name} · {a.phone}</div>
                  <div className="text-sm text-zinc-600 break-words">{a.address_line}, {a.parish}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setEditing(a)} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`edit-addr-${a.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(a)} className="p-1.5 hover:bg-red-50 text-red-500 rounded" data-testid={`delete-addr-${a.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg">{editing.id ? "Edit address" : "New address"}</div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-zinc-100 rounded">×</button>
            </div>
            <div className="space-y-3">
              <AddrField label="Label (Home, Work…)">
                <input className="input" value={editing.label}
                       onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                       data-testid="addr-label" />
              </AddrField>
              <AddrField label="Recipient name">
                <input className="input" value={editing.name}
                       onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                       data-testid="addr-name" />
              </AddrField>
              <AddrField label="Phone">
                <input className="input" value={editing.phone}
                       onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                       data-testid="addr-phone" />
              </AddrField>
              <AddrField label="Street address">
                <input className="input" value={editing.address_line}
                       onChange={(e) => setEditing({ ...editing, address_line: e.target.value })}
                       data-testid="addr-line" />
              </AddrField>
              <AddrField label="Parish">
                <select className="input" value={editing.parish}
                        onChange={(e) => setEditing({ ...editing, parish: e.target.value })}
                        data-testid="addr-parish">
                  <option>Kingston</option>
                  <option>Saint Catherine</option>
                </select>
              </AddrField>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_default}
                       onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })}
                       data-testid="addr-default" />
                Set as default
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="save-addr-btn">
                {editing.id ? "Save changes" : "Save address"}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`.input { width:100%; border:1px solid #e4e4e7; border-radius:0.5rem; padding:0.5rem 0.75rem; font-size:0.875rem; }`}</style>
    </div>
  );
}
function AddrField({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-600 block mb-1">{label}</label>
      {children}
    </div>
  );
}
